/**
 * NEXUMA — App Tour
 *
 * A self-contained, dependency-free guided tour (spotlight + tooltip card) built
 * on MUI. Role-aware (admin vs employee). Nuk niset vetë — aktivizohet vetëm
 * nga butoni "?" në AppBar (useTour().start('full')).
 *
 * Usage:
 *   <TourProvider><Dashboard /></TourProvider>     // wrap the app shell
 *   const tour = useTour(); tour.start();          // re-launch manually
 *   const { isActive } = useTour();                // e.g. force the drawer open
 */

import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef, useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import globals from '../../services/globals';
import { buildTourSteps } from './tourSteps';
import { buildPageTour } from './pageTour';

const TOUR_VERSION = 2;
const doneKey = (uid) => `nexuma_tour_done_v${TOUR_VERSION}_${uid || 'anon'}`;

const CARD_W = 360;
const SPOT_PAD = 12;
const GAP = 40;
const MARGIN = 10;

// Brand palette (matches the vivid cyan spotlight ring)
const BRAND = '#06B6D4';
const BRAND_DARK = '#0E7490';
const BRAND_GRAD = `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`;

const TYPE_MS = 42; // typewriter speed per character (slower = easier to read)

// Auto-advance delay (ms) — long enough for the text to finish typing + read.
// Pace is ~30% slower than a brisk read so users have time to absorb each step.
function stepDelay(step) {
  if (!step) return 7800;
  const chars = (step.body || '').length;
  let d = chars * TYPE_MS + 4680;             // typing time + reading buffer
  d = Math.max(7150, Math.min(d, 22100));
  if (step.route) d += 910;                    // give navigated pages time to render
  return d;
}

// ── Demo-mode actions ─────────────────────────────────────────────────────────
// The auto-demo only FILLS fields and OPENS/reveals things. It must never
// trigger anything that persists, sends or destroys data, so it stays safe on a
// live system. This guard blocks auto-clicking any save/submit/delete control.
const UNSAFE_RE = /\b(ruaj|save|d[ëe]rgo|dergo|submit|send|fshi|delete|hiq|remove|konfirmo|confirm|anulo|cancel|paguaj|pay|aprovo|approve|refuzo|reject|logout|dil)\b/i;

function setNativeValue(el, value) {
  const proto = el.tagName === 'TEXTAREA' ? window.HTMLTextAreaElement.prototype : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, 'value')?.set;
  if (setter) setter.call(el, value); else el.value = value;
  el.dispatchEvent(new Event('input', { bubbles: true }));
}

// Professional, realistic sample text. The values are fictional but look real
// ("Filan Fisteku" = the Albanian "John Doe"; example.com is the reserved demo
// domain) — the "sample, not saved" badge on the card marks them as made-up, and
// the demo never saves. `labelHint` carries the field's visible label so we match
// even when name/placeholder/aria-label are empty.
function sampleValue(inp, labelHint = '') {
  const type = (inp.getAttribute('type') || 'text').toLowerCase();
  const inputMode = (inp.getAttribute('inputmode') || '').toLowerCase();
  const hint = `${labelHint} ${inp.getAttribute('name') || ''} ${inp.getAttribute('placeholder') || ''} ${inp.getAttribute('aria-label') || ''}`.toLowerCase();

  if (type === 'email' || /email/.test(hint)) return 'filan.fisteku@example.com';
  if (type === 'date' || /dat[ëae]|date|lindj|birth/.test(hint)) return new Date().toISOString().slice(0, 10);

  // Numeric fields ALWAYS get a number, whatever the label says.
  const numeric = type === 'number' || inputMode === 'numeric' || inputMode === 'decimal';
  if (numeric) {
    if (/postal|zip|posta/.test(hint)) return '10000';
    if (/personal|pasapor|passport|nid|\bid\b/.test(hint)) return '1234567890';
    if (/tel|phone|telefon|mobil/.test(hint)) return '044123456';
    if (/paga|salary|page|bruto|neto/.test(hint)) return '850';
    if (/or[ëe]|hours/.test(hint)) return '8';
    if (/vit|year|mosh|age/.test(hint)) return '30';
    if (/sasi|amount|qty|numri/.test(hint)) return '5';
    return '10';
  }

  // Identity / passport numbers (text inputs) come before name branches.
  if (/personal|pasapor|passport|nid|\bid\b|numri person/.test(hint)) return '1234567890';
  if (/postal|zip|kodi post/.test(hint)) return '10000';
  if (/tel|phone|telefon|mobil/.test(hint)) return '044 123 456';
  if (/shtet|country|state|komb/.test(hint)) return 'Kosovë';
  if (/qytet|city|komun|municipal/.test(hint)) return 'Prishtinë';
  if (/adres|address|rrug|street/.test(hint)) return 'Rr. Agim Ramadani, Prishtinë';
  if (/vendban|vendlindj|vendi i lindj|birth\s*place/.test(hint)) return 'Prishtinë';
  if (/mbiemri|surname|last\s*name/.test(hint)) return 'Fisteku';
  if (/\bemri\b|emri i|first\s*name|\bname\b/.test(hint)) return 'Filan';
  if (/profesion|pozit|pozicion|position|title|vendi i pun|job/.test(hint)) return 'Specialist HR';
  if (/or[ëe]|hours|sasi|amount/.test(hint)) return '8';
  return 'Filan Fisteku';
}

// Resolve the concrete clickable / fillable node inside the step target.
function actionTarget(el, type) {
  if (type === 'fill') {
    return el.matches('input,textarea') ? el : el.querySelector('input:not([type="hidden"]),textarea');
  }
  return el.matches('button,a,[role="button"],[role="tab"],[aria-haspopup]')
    ? el
    : (el.querySelector('button,a,[role="button"]') || el);
}

const TourContext = createContext({ isActive: false, start: () => {}, stop: () => {} });
export const useTour = () => useContext(TourContext);

function resolveRole() {
  const user = globals().getLogedInUser();
  const uid = user?.data?.id || user?.id;
  const roles = (user?.data?.roles || user?.roles || [])
    .map((r) => (r?.name || r?.role_name || r || '').toString().toLowerCase().trim())
    .filter(Boolean);
  const isAdmin = roles.some((r) => r === 'admin' || r === 'menaxher' || r === 'manager');
  return { uid, role: roles.length > 0 && !isAdmin ? 'worker' : 'admin' };
}

export function TourProvider({ children }) {
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { uid, role } = resolveRole();

  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(true);    // auto-advance on by default
  const [rect, setRect] = useState(null);          // spotlight rect (viewport coords) or null
  const [card, setCard] = useState({ w: CARD_W, h: 200 });

  const stepsRef = useRef([]);
  const cardRef = useRef(null);
  const advanceRef = useRef(() => {});
  const typingRef = useRef(null);        // active fill-typing interval
  const actedIndexRef = useRef(-1);      // last step index we performed an action on
  const playingRef = useRef(true);
  playingRef.current = playing;          // so the locate effect can read it without re-running

  const steps = active ? stepsRef.current : [];
  const step = steps[index];
  const total = steps.length;

  // Perform a step's demo action (fill / open) on the located element.
  const runDemoAction = useCallback((stp, el) => {
    const action = stp && stp.action;
    if (!action || !el) return;
    const type = typeof action === 'string' ? action : action.type;
    const tgt = actionTarget(el, type);
    if (!tgt) return;
    if (type === 'fill') {
      if (tgt.disabled || tgt.readOnly || tgt.type === 'password' || tgt.value) return;
      const labelHint = `${stp.title || ''} ${stp.label || ''}`;
      const val = (typeof action === 'object' && action.value != null) ? action.value : sampleValue(tgt, labelHint);
      try { tgt.focus({ preventScroll: true }); } catch (_) {}
      const fieldType = (tgt.getAttribute('type') || 'text').toLowerCase();
      // Only free-text fields get the char-by-char "typing" effect; date/number/
      // email need a complete value or the browser rejects partial input.
      if (fieldType === 'text' || fieldType === 'search' || tgt.tagName === 'TEXTAREA') {
        if (typingRef.current) clearInterval(typingRef.current);
        let i = 0;
        typingRef.current = setInterval(() => {
          i += 1;
          setNativeValue(tgt, val.slice(0, i));
          if (i >= val.length) { clearInterval(typingRef.current); typingRef.current = null; }
        }, 48);
      } else {
        setNativeValue(tgt, val);
      }
    } else if (type === 'click' || type === 'open') {
      const text = `${tgt.textContent || ''} ${tgt.getAttribute ? (tgt.getAttribute('aria-label') || '') : ''}`;
      if (UNSAFE_RE.test(text)) return;            // never auto-trigger save/submit/delete
      try { tgt.click(); } catch (_) {}
    }
  }, []);

  const stop = useCallback((markDone = true) => {
    setActive(false);
    setRect(null);
    setIndex(0);
    if (markDone && uid) { try { localStorage.setItem(doneKey(uid), '1'); } catch (_) {} }
  }, [uid]);

  // mode: 'page' (default) walks through the current page / open form;
  //       'full' runs the whole-app guided onboarding tour.
  const start = useCallback((mode = 'page') => {
    stepsRef.current = mode === 'full' ? buildTourSteps(role, t) : buildPageTour(t);
    setRect(null);
    setIndex(0);
    setPlaying(true);
    setActive(true);
  }, [role, t]);

  const next = useCallback(() => {
    setIndex((i) => (i >= stepsRef.current.length - 1 ? (stop(true), i) : i + 1));
  }, [stop]);
  const back = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);
  advanceRef.current = next;

  // ── Auto-start i HEQUR me kërkesë të përdoruesit ─────────────────────────────
  // Turi nuk niset më vetë pas login-it — aktivizohet vetëm manualisht nga
  // butoni "?" në AppBar (tour.start('full')).

  // ── Locate the current step's target (navigate, scroll, measure) ─────────────
  useEffect(() => {
    if (!active || !step) return undefined;
    let cancelled = false;
    let attempts = 0;

    if (step.route) {
      const hash = (window.location.hash || '').replace(/^#/, '') || '/';
      const curPath = hash.split('?')[0];
      if (curPath !== step.route.split('?')[0]) history.push(step.route);
    }

    const resolveEl = () => {
      if (step.el) return document.contains(step.el) ? step.el : null;
      if (step.target) return document.querySelector(step.target);
      return null;
    };

    const locate = () => {
      if (cancelled) return;
      attempts += 1;
      if (!step.target && !step.el) { setRect(null); return; }
      const el = resolveEl();
      if (el) {
        el.scrollIntoView({ block: 'center', inline: 'nearest' });
        // Auto-select / focus the element being explained (desktop only, so we
        // don't pop the mobile keyboard). Inputs show their caret, buttons/links
        // show their focus ring — making the "current" control obvious.
        if (!isMobile && !step.page) {
          const focusable = el.matches('input, textarea, select, button, a, [tabindex]')
            ? el
            : el.querySelector('input, textarea, select, button, a, [tabindex]');
          if (focusable) { try { focusable.focus({ preventScroll: true }); } catch (_) {} }
        }
        requestAnimationFrame(() => {
          if (cancelled) return;
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        });
        // ── Demo mode: after the highlight lands, perform the step's action
        //    (fill a field / open something) once, while auto-play is on. ──────
        if (!isMobile && playingRef.current && step.action && actedIndexRef.current !== index) {
          actedIndexRef.current = index;
          setTimeout(() => { if (!cancelled) runDemoAction(step, el); }, 700);
        }
      } else if (attempts < 25) {
        setTimeout(locate, 120);
      } else if (step.skipIfMissing) {
        advanceRef.current();
      } else {
        setRect(null);
      }
    };

    const tmr = setTimeout(locate, step.route ? 380 : 60);
    return () => {
      cancelled = true;
      clearTimeout(tmr);
      if (typingRef.current) { clearInterval(typingRef.current); typingRef.current = null; }
    };
  }, [active, index]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keep the spotlight aligned on resize / scroll ────────────────────────────
  useEffect(() => {
    if (!active || (!step?.target && !step?.el)) return undefined;
    const update = () => {
      const el = step.el
        ? (document.contains(step.el) ? step.el : null)
        : document.querySelector(step.target);
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [active, index]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Keyboard: Esc skips, Enter / → advance, ← goes back ──────────────────────
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); stop(true); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); next(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); back(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, next, back, stop]);

  // ── Auto-advance (play) — moves to the next step on a timer ──────────────────
  useEffect(() => {
    if (!active || !playing || !step) return undefined;
    const tmr = setTimeout(() => next(), stepDelay(step));
    return () => clearTimeout(tmr);
  }, [active, index, playing, step, next]);

  // ── Measure card HEIGHT to position it accurately ────────────────────────────
  // Width is fixed (CARD_W), so we never measure it — measuring a value we then
  // feed back into the width was causing a shrink-to-nothing feedback loop while
  // the entrance animation was mid-transform. offsetHeight ignores transforms.
  useLayoutEffect(() => {
    if (!active || !cardRef.current) return;
    const h = cardRef.current.offsetHeight;
    if (Math.abs(h - card.h) > 2) setCard((c) => ({ ...c, h }));
  }); // run after every render while active

  const ctx = { isActive: active, start, stop };

  return (
    <TourContext.Provider value={ctx}>
      {children}
      {active && step && createPortal(
        <TourOverlay
          step={step}
          rect={rect}
          index={index}
          total={total}
          card={card}
          cardRef={cardRef}
          isMobile={isMobile}
          playing={playing}
          delay={stepDelay(step)}
          onTogglePlay={() => setPlaying((p) => !p)}
          t={t}
          onNext={next}
          onBack={back}
          onSkip={() => stop(true)}
        />,
        document.body,
      )}
    </TourContext.Provider>
  );
}

// Area (px²) by which two rects overlap — 0 means they don't touch.
function overlapArea(a, b) {
  const x = Math.max(0, Math.min(a.right, b.right) - Math.max(a.left, b.left));
  const y = Math.max(0, Math.min(a.bottom, b.bottom) - Math.max(a.top, b.top));
  return x * y;
}

// Candidate card position for a given side, clamped to the viewport.
function placeFor(side, rect, w, h, vw, vh) {
  let top;
  let left;
  if (side === 'bottom') { top = rect.top + rect.height + GAP; left = rect.left + rect.width / 2 - w / 2; }
  else if (side === 'top') { top = rect.top - h - GAP; left = rect.left + rect.width / 2 - w / 2; }
  else if (side === 'right') { top = rect.top + rect.height / 2 - h / 2; left = rect.left + rect.width + GAP; }
  else { top = rect.top + rect.height / 2 - h / 2; left = rect.left - w - GAP; }
  left = Math.min(Math.max(MARGIN, left), vw - w - MARGIN);
  top = Math.min(Math.max(MARGIN, top), vh - h - MARGIN);
  // arrow points from the card edge toward the element → opposite of the side name
  const arrowSide = { bottom: 'top', top: 'bottom', right: 'left', left: 'right' }[side];
  return { top, left, side: arrowSide };
}

// ── Position the tooltip card relative to the spotlight rect ────────────────────
function computeCardPos(rect, placement, card, isMobile) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const w = Math.min(CARD_W, vw - MARGIN * 2);   // fixed width — never measured
  const h = card.h;

  // No target → center on screen (desktop); mobile → fixed bottom sheet.
  if (!rect || isMobile) {
    return {
      left: Math.max(MARGIN, (vw - w) / 2),
      top: isMobile ? vh - h - MARGIN - 56 : Math.max(MARGIN, (vh - h) / 2),
      side: null,
    };
  }

  let top;
  let left;
  let side = null; // which card edge faces the element → where the arrow points
  switch (placement) {
    case 'auto': {
      // Try every side and pick the one whose card overlaps the highlighted
      // element the least — so the card never sits on top of what it explains.
      const elRect = { left: rect.left, top: rect.top, right: rect.left + rect.width, bottom: rect.top + rect.height };
      let best = null;
      ['right', 'bottom', 'left', 'top'].forEach((s) => {
        const cand = placeFor(s, rect, w, h, vw, vh);
        const cardRect = { left: cand.left, top: cand.top, right: cand.left + w, bottom: cand.top + h };
        const ov = overlapArea(cardRect, elRect);
        if (!best || ov < best.ov) best = { ...cand, ov };
      });
      return { left: best.left, top: best.top, w, side: best.side };
    }
    case 'page':
      // Whole-page spotlight → dock the card in the TOP-right corner (below the
      // AppBar) so it never runs off the bottom of the screen and the page stays
      // visible while the user reads the instructions.
      top = 72;
      left = vw - w - GAP;
      break;
    case 'bottom':
      top = rect.top + rect.height + GAP;
      left = rect.left + rect.width / 2 - w / 2;
      side = 'top';
      break;
    case 'left':
      top = rect.top + rect.height / 2 - h / 2;
      left = rect.left - w - GAP;
      side = 'right';
      break;
    case 'top':
      top = rect.top - h - GAP;
      left = rect.left + rect.width / 2 - w / 2;
      side = 'bottom';
      break;
    case 'right':
    default:
      top = rect.top;
      left = rect.left + rect.width + GAP;
      side = 'left';
      break;
  }

  // Clamp inside the viewport
  left = Math.min(Math.max(MARGIN, left), vw - w - MARGIN);
  top = Math.min(Math.max(MARGIN, top), vh - h - MARGIN);
  return { left, top, w, side };
}

// Where the pointer arrow sits on the card edge, aligned to the element centre.
// Returns the centre offset (along the facing edge) from the card's top-left.
function computeArrowPos(pos, rect, card) {
  if (!pos.side || !rect) return null;
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  if (pos.side === 'top' || pos.side === 'bottom') {
    return { side: pos.side, center: Math.min(Math.max(cx - pos.left, 22), pos.w - 22) };
  }
  return { side: pos.side, center: Math.min(Math.max(cy - pos.top, 22), card.h - 22) };
}

// Build the CSS for an elongated triangular pointer aimed at the element.
function arrowSxFor(arrow, pos, w, cardH) {
  const BASE = 10;  // half-width of the arrow's base
  const LEN = 26;   // how far the arrow extends toward the element
  const common = {
    position: 'absolute',
    width: 0,
    height: 0,
    filter: 'drop-shadow(0 3px 6px rgba(15,23,42,0.16))',
    transition: 'top 0.2s ease, left 0.2s ease',
  };
  switch (arrow.side) {
    case 'left':
      return { ...common, left: pos.left - LEN, top: pos.top + arrow.center - BASE,
        borderTop: `${BASE}px solid transparent`, borderBottom: `${BASE}px solid transparent`, borderRight: `${LEN}px solid #fff` };
    case 'right':
      return { ...common, left: pos.left + w, top: pos.top + arrow.center - BASE,
        borderTop: `${BASE}px solid transparent`, borderBottom: `${BASE}px solid transparent`, borderLeft: `${LEN}px solid #fff` };
    case 'top':
      return { ...common, left: pos.left + arrow.center - BASE, top: pos.top - LEN,
        borderLeft: `${BASE}px solid transparent`, borderRight: `${BASE}px solid transparent`, borderBottom: `${LEN}px solid #fff` };
    case 'bottom':
    default:
      return { ...common, left: pos.left + arrow.center - BASE, top: pos.top + cardH,
        borderLeft: `${BASE}px solid transparent`, borderRight: `${BASE}px solid transparent`, borderTop: `${LEN}px solid #fff` };
  }
}

function TourOverlay({ step, rect, index, total, card, cardRef, isMobile, playing, delay, onTogglePlay, t, onNext, onBack, onSkip }) {
  const pos = computeCardPos(rect, step.placement, card, isMobile);
  const w = Math.min(CARD_W, window.innerWidth - MARGIN * 2);
  const isLast = index === total - 1;
  const isFill = step.action === 'fill' || step.action?.type === 'fill';
  const showSpot = Boolean(rect);
  const arrow = computeArrowPos(pos, rect, card);
  // Small near-square targets (icon buttons, avatars) get a circular highlight;
  // everything else keeps a soft rounded-rectangle.
  const spotRound = rect && Math.abs(rect.width - rect.height) <= 10 && Math.max(rect.width, rect.height) <= 72;
  const spotRadius = spotRound ? '50%' : '12px';

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 2000 }}>
      {/* Backdrop — captures clicks so the underlying app isn't interacted with */}
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          position: 'absolute',
          inset: 0,
          // When there's no spotlight, dim with a flat backdrop; otherwise the
          // spotlight box draws the dim area via its huge box-shadow. Page steps
          // keep a lighter dim so the actual page stays readable.
          bgcolor: showSpot ? 'transparent' : (step.page ? 'rgba(15,23,42,0.30)' : 'rgba(15,23,42,0.55)'),
        }}
      />

      {/* Spotlight cut-out */}
      {showSpot && (
        <Box
          sx={{
            position: 'absolute',
            top: rect.top - SPOT_PAD,
            left: rect.left - SPOT_PAD,
            width: rect.width + SPOT_PAD * 2,
            height: rect.height + SPOT_PAD * 2,
            borderRadius: spotRadius,
            pointerEvents: 'none',
            // Solid ring so the focused element clearly stands out from the page.
            border: '3px solid rgba(6,182,212,1)',
            transition: 'all 0.2s ease',
            // Dim the rest of the screen + a pulsing halo around the selected element.
            '@keyframes tourRing': {
              '0%':   { boxShadow: '0 0 0 9999px rgba(15,23,42,0.58), 0 0 0 0 rgba(6,182,212,0.95)' },
              '70%':  { boxShadow: '0 0 0 9999px rgba(15,23,42,0.58), 0 0 0 22px rgba(6,182,212,0.15)' },
              '100%': { boxShadow: '0 0 0 9999px rgba(15,23,42,0.58), 0 0 0 30px rgba(6,182,212,0)' },
            },
            boxShadow: '0 0 0 9999px rgba(15,23,42,0.58)',
            animation: 'tourRing 1.4s ease-out infinite',
          }}
        />
      )}

      {/* Pointer arrow — an elongated white triangle whose base meets the card
          edge and whose tip extends toward the element being explained. */}
      {arrow && <Box sx={arrowSxFor(arrow, pos, w, card.h)} />}

      {/* Tooltip card */}
      <Paper
        ref={cardRef}
        elevation={0}
        sx={{
          position: 'absolute',
          top: pos.top,
          left: pos.left,
          width: w,
          maxWidth: `calc(100vw - ${MARGIN * 2}px)`,
          pt: 2.25,
          px: 2.25,
          pb: 1.75,
          borderRadius: '18px',
          bgcolor: '#fff',
          border: '1px solid rgba(15,23,42,0.06)',
          boxShadow: '0 24px 60px -12px rgba(15,23,42,0.32), 0 8px 20px -8px rgba(6,182,212,0.20)',
          transition: 'top 0.22s cubic-bezier(0.22,1,0.36,1), left 0.22s cubic-bezier(0.22,1,0.36,1)',
          overflow: 'hidden',
          '@keyframes tourPop': {
            from: { opacity: 0, transform: 'scale(0.96) translateY(6px)' },
            to:   { opacity: 1, transform: 'scale(1) translateY(0)' },
          },
          animation: 'tourPop 0.26s cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        {/* Auto-advance progress bar (brand gradient) */}
        {playing && (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: 3,
              backgroundImage: BRAND_GRAD,
              width: '0%',
              '@keyframes tourGrow': { from: { width: '0%' }, to: { width: '100%' } },
              animation: `tourGrow ${delay}ms linear forwards`,
            }}
          />
        )}

        {/* Header — icon badge · title · close */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1 }}>
          <Box
            sx={{
              flexShrink: 0,
              width: 30,
              height: 30,
              borderRadius: '9px',
              backgroundImage: BRAND_GRAD,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 10px rgba(6,182,212,0.35)',
            }}
          >
            <AutoAwesomeRoundedIcon sx={{ fontSize: 17, color: '#fff' }} />
          </Box>
          <Typography sx={{ flex: 1, fontWeight: 700, fontSize: '1rem', lineHeight: 1.3, color: '#0F172A', mt: '3px' }}>
            {step.title}
          </Typography>
          <IconButton size="small" onClick={onSkip} aria-label="close-tour" sx={{ m: -0.5, color: 'text.disabled' }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        <Typography component="div" sx={{ fontSize: '0.875rem', color: '#475569', lineHeight: 1.55, mb: isFill ? 1 : 1.75, minHeight: '2.6em' }}>
          <Typewriter key={index} text={step.body} paused={!playing} />
        </Typography>

        {/* Fictional-sample marker — shown whenever the demo auto-fills a field */}
        {isFill && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.4,
              mb: 1.75,
              borderRadius: '8px',
              bgcolor: 'rgba(245,158,11,0.12)',
              color: '#B45309',
              fontSize: '0.72rem',
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {t('tour.dyn.sampleTag')}
          </Box>
        )}

        <Box sx={{ borderTop: '1px solid', borderColor: 'rgba(15,23,42,0.07)', mx: -2.25, mb: 1.25 }} />

        {/* Meta row — autoplay toggle · step pill · "don't show again" */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
            <Tooltip title={playing ? t('tour.pause') : t('tour.play')} arrow>
              <IconButton
                size="small"
                onClick={onTogglePlay}
                aria-label="toggle-autoplay"
                sx={{ ml: -0.5, color: BRAND_DARK, '&:hover': { bgcolor: 'rgba(6,182,212,0.08)' } }}
              >
                {playing ? <PauseIcon sx={{ fontSize: 18 }} /> : <PlayArrowIcon sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
            <Box
              sx={{
                px: 1,
                py: '3px',
                borderRadius: '999px',
                bgcolor: 'rgba(6,182,212,0.10)',
                color: BRAND_DARK,
                fontSize: '0.7rem',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                letterSpacing: '0.01em',
              }}
            >
              {t('tour.stepOf', { current: index + 1, total })}
            </Box>
          </Box>
          <Button
            onClick={onSkip}
            size="small"
            sx={{ textTransform: 'none', color: 'text.disabled', fontSize: '0.72rem', fontWeight: 600, minWidth: 0, px: 0.75, whiteSpace: 'nowrap', flexShrink: 0 }}
          >
            {t('tour.dontShow')}
          </Button>
        </Box>

        {/* Action row — skip on the left, back / next on the right */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            {!isLast && (
              <Button onClick={onSkip} size="small" sx={{ textTransform: 'none', color: 'text.secondary', whiteSpace: 'nowrap', minWidth: 0 }}>
                {t('tour.skip')}
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
            {index > 0 && (
              <Button
                onClick={onBack}
                size="small"
                variant="outlined"
                sx={{
                  textTransform: 'none',
                  borderRadius: '10px',
                  whiteSpace: 'nowrap',
                  borderColor: 'rgba(15,23,42,0.18)',
                  color: '#334155',
                  '&:hover': { borderColor: BRAND, bgcolor: 'rgba(6,182,212,0.06)' },
                }}
              >
                {t('tour.back')}
              </Button>
            )}
            <Button
              onClick={onNext}
              size="small"
              variant="contained"
              disableElevation
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                fontWeight: 700,
                px: 2,
                whiteSpace: 'nowrap',
                color: '#fff',
                backgroundImage: BRAND_GRAD,
                boxShadow: '0 6px 16px rgba(6,182,212,0.38)',
                '&:hover': { backgroundImage: BRAND_GRAD, boxShadow: '0 8px 20px rgba(6,182,212,0.48)' },
              }}
            >
              {isLast ? t('tour.finish') : t('tour.next')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

// Reveals text one character at a time (typewriter), with a blinking caret.
// Remount per step via key={index}; pause freezes the typing.
function Typewriter({ text, paused }) {
  const [n, setN] = useState(0);
  const safe = text || '';

  useEffect(() => {
    if (paused || n >= safe.length) return undefined;
    const id = setInterval(() => {
      setN((prev) => {
        if (prev >= safe.length) { clearInterval(id); return prev; }
        return prev + 1;
      });
    }, TYPE_MS);
    return () => clearInterval(id);
  }, [paused, safe, n]);

  const done = n >= safe.length;
  return (
    <>
      {safe.slice(0, n)}
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          width: '2px',
          height: '1.05em',
          ml: '2px',
          verticalAlign: 'text-bottom',
          bgcolor: 'primary.main',
          opacity: done ? 0 : 1,
          '@keyframes tourCaret': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0 } },
          animation: done ? 'none' : 'tourCaret 0.9s step-end infinite',
        }}
      />
      {/* Reserve the full text's height from the first frame so the card never
          grows downward as it types (which used to push it off-screen). */}
      <Box component="span" aria-hidden sx={{ opacity: 0 }}>{safe.slice(n)}</Box>
    </>
  );
}

export default TourProvider;
